import { z } from "zod";
import { StatusTypes } from '../../../features/pms-reservation/types/StatusTypes';
import { MarkTypes } from '../../../features/pms-reservation/types/MarkTypes';
import { PaymentTypes } from '../../../features/pms-reservation/types/PaymentTypes';
import { parse, isValid, isBefore } from "date-fns";

// Skema untuk satu kamar, mirip dengan yang lama, tapi akan digunakan secara internal
const BookingRoomV4Schema = z.object({
    invoice_number: z.string().optional(), // Boleh ada (untuk update) atau tidak (untuk create)
    payment_type: z.enum(PaymentTypes.map((p) => p.value) as [string, ...string[]], { errorMap: () => ({ message: 'Please select a payment type' }) }),
    customer_name: z.string().min(1, 'Please enter the guest\'s first name').max(255, 'First name must be at most 255 characters'),
    customer_surname: z.string().max(255, 'Surname must be at most 255 characters').optional(),
    customer_email: z.string().min(1, 'Please enter the guest\'s email address').email('Please enter a valid email address, e.g. name@example.com'),
    checkin_date: z.string().min(1, 'Please select a check-in date'),
    checkout_date: z.string().min(1, 'Please select a check-out date'),
    room_type_id: z.string().uuid('Please select a room type for this room'),
    // Empty string ('' = nothing selected yet) fails .uuid(), so the message must say what to do.
    rate_plan_id: z.string().uuid('Please select a rate plan for this room').optional().nullable(),
    room_id: z.string().uuid('Please select a valid room number, or leave it unassigned').optional().nullable(),
    adults: z.coerce.number({ invalid_type_error: 'Please enter the number of adults' }).int('Number of adults must be a whole number').min(1, 'At least 1 adult is required'),
    children: z.coerce.number({ invalid_type_error: 'Please enter the number of children' }).int('Number of children must be a whole number').min(0, 'Number of children cannot be negative'),
    rates: z.array(z.object({
        date: z.string(),
        rate: z.coerce.number({ invalid_type_error: 'Please enter a valid rate amount' }).min(0, 'Rate cannot be negative'),
    })),
    // Pembayaran sekarang berada di dalam setiap booking room
    payments: z.array(z.object({
        date_payment: z.string(),
        payment_type: z.string(),
        amount: z.coerce.number({ invalid_type_error: 'Please enter a valid payment amount' }).int('Payment amount must be a whole number').min(0, 'Payment amount cannot be negative'),
        surcharge: z.coerce.number({ invalid_type_error: 'Please enter a valid surcharge amount' }).int('Surcharge must be a whole number').min(0, 'Surcharge cannot be negative'),
    })).optional(),

    status: z.enum(StatusTypes.map((s) => s.value) as [string, ...string[]], { errorMap: () => ({ message: 'Please select a reservation status' }) }),
    mark: z.enum(MarkTypes.map((m) => m.value) as [string, ...string[]], { errorMap: () => ({ message: 'Please select a valid mark' }) }).optional().nullable(),
});

// Skema utama yang digunakan oleh react-hook-form
export const CreateBookingFormSchemaV4 = z.object({
    // Data level atas yang akan diduplikasi ke setiap request
    property_id: z.string().uuid('No active property selected. Please select a property and try again'), // Wajib ada
    invoice_date: z.string().min(1, 'Please select an invoice date'),
    public_note: z.string().optional(),
    private_note: z.string().optional(),
    customer_phone: z.string().max(20, 'Phone number must be at most 20 characters').optional().nullable(),
    customer_address: z.string().max(255, 'Address must be at most 255 characters').optional().nullable(),
    customer_country: z.string().max(255, 'Country must be at most 255 characters').optional().nullable(),
    customer_state: z.string().max(255, 'State must be at most 255 characters').optional().nullable(),
    customer_city: z.string().max(255, 'City must be at most 255 characters').optional().nullable(),
    customer_zip: z.string().max(32, 'Postal code must be at most 32 characters').optional().nullable(),
    customer_date_of_birth: z.string().optional().nullable(),
    customer_identity_number: z.string().max(50, 'Identity number must be at most 50 characters').optional().nullable(),
    // agent_id: z.string().uuid().optional().nullable(),

    // UUID agent, atau literal "direct". Pakai refine (bukan z.union) supaya pesan error
    // kita yang tampil — z.union meneruskan "Invalid uuid" dari cabang pertama.
    agent_id: z.string()
        .refine((v) => v === 'direct' || z.string().uuid().safeParse(v).success, 'Please select a valid agent, or choose Direct')
        .optional().nullable(),

    // Array kamar yang dikelola oleh UI
    bookingroom: z.array(BookingRoomV4Schema),

}).superRefine((data, ctx) => {
    // Validasi bahwa checkout date harus setelah checkin date
    data.bookingroom.forEach((room, index) => {
        const checkinDate = parse(room.checkin_date, 'yyyy-MM-dd', new Date());
        const checkoutDate = parse(room.checkout_date, 'yyyy-MM-dd', new Date());

        if (isValid(checkinDate) && isValid(checkoutDate) && isBefore(checkoutDate, checkinDate)) {
            ctx.addIssue({
                path: ['bookingroom', index, 'checkout_date'],
                message: 'Check-out date must be after the check-in date',
                code: z.ZodIssueCode.custom,
            });
        }

        // Validasi: Jika room_id ada (assigned), maka rate_plan_id harus ada
        if (room.room_id && !room.rate_plan_id) {
            ctx.addIssue({
                path: ['bookingroom', index, 'rate_plan_id'],
                message: 'Please select a rate plan for this room',
                code: z.ZodIssueCode.custom,
            });
        }
    });
});

export type CreateBookingFormV4 = z.infer<typeof CreateBookingFormSchemaV4>;